import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  FormControl,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { Delete, History } from '@mui/icons-material';
import { useEffect, useRef } from 'react';
import { loadBillMeHistory, loadBillMeService, SaveBillMe } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';

export default function BillMe() {
  const [registration, setRegistration] = useState({
    name: '',
    gender: '',
    tel: '',
    location: '',
  });

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [openHistory, setOpenHistory] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [currentUser, setcurrentUser] = useState();

  // Loading states
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const registrationRef = useRef(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [requestHistory, setRequestHistory] = useState([]);

  const loadHis = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.post(loadBillMeHistory, { currentUser });

      if (response.data.success) {
        setRequestHistory(response.data.results || response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      setRequestHistory([]);
      setSnackbarMessage('Failed to load history');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openhi = () => {
    loadHis();
    setOpenHistory(true);
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const handleRegistrationChange = (field, value) => {
    setRegistration({ ...registration, [field]: value });
  };

  const handleCheckboxChange = (id) => {
    setAvailableItems(
      availableItems.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const handleAmountChange = (id, newAmount) => {
    setAvailableItems(
      availableItems.map((item) =>
        item.id === id ? { ...item, amount: parseFloat(newAmount) || 0 } : item
      )
    );
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleAddSelected = () => {
    const checkedItems = availableItems.filter((item) => item.checked);
    setSelectedItems([...selectedItems, ...checkedItems]);
    setAvailableItems(availableItems.map((item) => ({ ...item, checked: false })));
  };

  const handleRemoveSelected = (id) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== id));
  };

  const [errors, setErrors] = useState({});

  const validateRegistration = () => {
    const newErrors = {};

    if (!registration.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (registration.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters long';
    }

    if (!registration.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!registration.tel.trim()) {
      newErrors.tel = 'Telephone number is required';
    } else if (!/^\d{10}$/.test(registration.tel)) {
      newErrors.tel = 'Telephone must be exactly 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      const userInfo = JSON.parse(savedUserInfo);
      const userName = userInfo.name || '';
      setcurrentUser(userName);
    }
  }, []);

  const handleSubmitRequest = async () => {
    const isValid = validateRegistration();
    if (!isValid) {
      registrationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setSnackbarMessage('⚠️ Please fix the highlighted errors before submitting.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (selectedItems.length === 0) {
      setSnackbarMessage('⚠️ Please select at least one item to request.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const result = await Swal.fire({
      title: 'Confirm / Cancel',
      text: 'Are You Sure You Want To Make This Request',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes Confirm',
    });

    if (result.isConfirmed) {
      setLoading(true);

      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      let hours = now.getHours();
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'P' : 'A';
      hours = hours % 12 || 12;
      const shortHour = String(hours).padStart(2, '0');
      const randomDigit = Math.floor(Math.random() * 10);
      let uniqueId = `${month}${day}${ampm}${shortHour}${minutes}${randomDigit}`;

      while (requestHistory.some((req) => req.id === uniqueId)) {
        uniqueId = `${month}${day}${ampm}${shortHour}${minutes}${Math.floor(Math.random() * 10)}`;
      }

      const request = {
        id: uniqueId,
        items: selectedItems.map((item) => ({
          name: item.name,
          amount: item.amount,
        })),
        totalAmount: selectedItems.reduce((sum, item) => sum + item.amount, 0),
        requestBy: currentUser,
        name: registration.name,
        gender: registration.gender,
        tel: registration.tel,
        location: registration.location,
        paidBy: '-',
      };

      try {
        const response = await axios.post(SaveBillMe, request);

        if (response.data.success) {
          setRequestHistory([request, ...requestHistory]);
          setSelectedItems([]);

          setSnackbarMessage(`✅ Request submitted! (ID: ${uniqueId})`);
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } else {
          throw new Error(response.data.message || 'Bill Me failed');
        }
      } catch (error) {
        console.error(error);
        setSnackbarMessage('❌ Failed to submit request');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredHistory = requestHistory.filter((item) => {
    const matchesSearch = item?.requestBy?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesId = item?.id?.toString().toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = dateFilter ? item?.date === dateFilter : true;

    return (matchesSearch || matchesId) && matchesDate;
  });

  const totalAmount = selectedItems.reduce((sum, item) => sum + item.amount, 0);

  const handleAddCustomItem = () => {
    if (!newItemName.trim() || !newItemAmount) return;

    const newItem = {
      id: Date.now(),
      name: newItemName.trim(),
      amount: parseFloat(newItemAmount),
      checked: false,
    };

    setAvailableItems([newItem, ...availableItems]);
    setNewItemName('');
    setNewItemAmount('');
  };

  const loadBillMe = async () => {
    setInitialLoading(true);
    try {
      const response = await axios.get(loadBillMeService);
      if (response.data.success) {
        setAvailableItems(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error',
      });
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadBillMe();
  }, []);

  const filteredItems = availableItems.filter((item) =>
    item.name.toLowerCase().includes(itemSearch.toLowerCase())
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Loading Backdrop */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Processing request...</Typography>
        </Box>
      </Backdrop>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
          Bill Me
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Make Patient Bill Payment Requests
        </Typography>
      </Box>

      {/* Initial Loading State */}
      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Loading Bill Me services...
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          {/* Registration Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }} ref={registrationRef}>
            <Typography gutterBottom sx={{ mb: 2 }}>
              Patient Information
            </Typography>

            <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
              <Grid item xs={12} sm={6} size={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={registration.name}
                  onChange={(e) => handleRegistrationChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name || ''}
                />
              </Grid>

              <Grid item xs={12} sm={6} size={6}>
                <FormControl
                  fullWidth
                  error={!!errors.gender}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <FormLabel>Gender</FormLabel>
                  <RadioGroup
                    row
                    value={registration.gender}
                    onChange={(e) => handleRegistrationChange('gender', e.target.value)}
                  >
                    <FormControlLabel value="male" control={<Radio />} label="Male" />
                    <FormControlLabel value="female" control={<Radio />} label="Female" />
                  </RadioGroup>
                  {errors.gender && (
                    <Typography color="error" variant="caption" sx={{ mt: -1 }}>
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} size={6}>
                <TextField
                  fullWidth
                  label="Telephone (10 digits)"
                  value={registration.tel}
                  onChange={(e) => handleRegistrationChange('tel', e.target.value.replace(/\D/g, ''))}
                  inputProps={{ maxLength: 10 }}
                  error={!!errors.tel}
                  helperText={errors.tel || ''}
                />
              </Grid>

              <Grid item xs={12} sm={6} size={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={registration.location}
                  onChange={(e) => handleRegistrationChange('location', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Make Request Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography>Patient Request</Typography>

              <Button variant="outlined" startIcon={<History />} onClick={openhi} size="small">
                View History
              </Button>
            </Box>

            {availableItems.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search available items..."
                  variant="outlined"
                  sx={{ mb: 2 }}
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                />

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ backgroundColor: 'background.default' }}>
                        <TableCell />
                        <TableCell>
                          <TextField
                            fullWidth
                            placeholder="Enter new item name"
                            size="small"
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              type="number"
                              placeholder="Amount"
                              size="small"
                              sx={{ width: 100 }}
                              value={newItemAmount}
                              onChange={(e) => setNewItemAmount(e.target.value)}
                            />
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={handleAddCustomItem}
                              disabled={!newItemName || !newItemAmount}
                            >
                              Add
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {filteredItems
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((item) => (
                          <TableRow key={item.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={item.checked}
                                onChange={() => handleCheckboxChange(item.id)}
                              />
                            </TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={item.amount}
                                onChange={(e) => handleAmountChange(item.id, e.target.value)}
                                size="small"
                                sx={{ width: 100 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>

                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </TableContainer>

                <Button
                  variant="contained"
                  onClick={handleAddSelected}
                  sx={{ mt: 2 }}
                  disabled={!availableItems.some((item) => item.checked)}
                >
                  Add Selected Items
                </Button>
              </Box>
            )}

            {selectedItems.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Selected Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.amount.toFixed(2)}</TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveSelected(item.id)}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell>
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{totalAmount.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmitRequest}
                  sx={{ mt: 2 }}
                  fullWidth
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Request History Dialog */}
      <Dialog open={openHistory} onClose={() => setOpenHistory(false)} fullWidth maxWidth="lg">
        <DialogTitle>Request History</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={12} sm={6} size={6}>
              <TextField
                fullWidth
                label="Search by Id , Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} size={6}>
              <TextField
                fullWidth
                type="date"
                label="Filter by Date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }} color="text.secondary">
                  Loading history...
                </Typography>
              </Box>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Request</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Request By</TableCell>
                    <TableCell>Paid By</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredHistory.length > 0 ? (
                    filteredHistory.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{item.request}</TableCell>
                        <TableCell>{item.date}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.status}
                            color={item.status === 'paid' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.requestBy}</TableCell>
                        <TableCell>{item.paidBy}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No history found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistory(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            boxShadow: 3,
            borderRadius: 2,
          }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}